import {
  OBRA_STATUS_LABELS,
  VISIT_STATUS_LABELS,
  type ObraStatus,
  type VisitStatus,
} from '@obras/shared';
import {
  CircleCheck,
  CircleDot,
  CircleSlash,
  Clock,
  PauseCircle,
  TriangleAlert,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';

export type Tone = 'muted' | 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'paused';

const TONES: Record<Tone, string> = {
  muted: 'bg-muted text-muted-foreground',
  primary: 'bg-primary-bg text-primary-strong',
  success: 'bg-success-bg text-success',
  warning: 'bg-warning-bg text-warning',
  danger: 'bg-danger-bg text-danger',
  info: 'bg-info-bg text-info',
  paused: 'bg-paused-bg text-paused',
};

/**
 * Badge sempre carrega ícone + texto.
 * Cor nunca é o único portador da informação — design.md §3.
 */
export function Badge({
  tone = 'muted',
  icon: Icon,
  children,
  className,
}: {
  tone?: Tone;
  icon?: LucideIcon;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-xs px-2 py-0.5 text-[12px] font-medium whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {Icon ? <Icon size={13} strokeWidth={1.75} className="shrink-0" aria-hidden /> : null}
      {children}
    </span>
  );
}

const OBRA_STATUS_STYLE: Record<ObraStatus, { tone: Tone; icon: LucideIcon }> = {
  em_andamento: { tone: 'primary', icon: CircleDot },
  paralisada: { tone: 'paused', icon: PauseCircle },
  concluida: { tone: 'success', icon: CircleCheck },
  cancelada: { tone: 'danger', icon: CircleSlash },
};

export function ObraStatusBadge({ status }: { status: ObraStatus }) {
  const style = OBRA_STATUS_STYLE[status];
  return (
    <Badge tone={style.tone} icon={style.icon}>
      {OBRA_STATUS_LABELS[status]}
    </Badge>
  );
}

const VISIT_STATUS_STYLE: Record<VisitStatus, { tone: Tone; icon: LucideIcon }> = {
  em_dia: { tone: 'success', icon: CircleCheck },
  vencendo: { tone: 'warning', icon: Clock },
  vencida: { tone: 'danger', icon: TriangleAlert },
  sem_visita: { tone: 'muted', icon: CircleDot },
  // Obra encerrada não tem próxima visita; o selo informa, não alerta.
  nao_se_aplica: { tone: 'muted', icon: CircleSlash },
};

export function VisitStatusBadge({ status }: { status: VisitStatus }) {
  const style = VISIT_STATUS_STYLE[status];
  return (
    <Badge tone={style.tone} icon={style.icon}>
      {VISIT_STATUS_LABELS[status]}
    </Badge>
  );
}
